/**
 * Fiche Google : écarts face aux concurrents les mieux notés, corrections,
 * visibilité (FicheDiagnosticService). Les statistiques Google restent dans
 * la vue d'ensemble.
 */
import React, { useCallback, useEffect, useState } from 'react';

function h() { return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, 'Content-Type': 'application/json' }; }

interface Ecart { type: string; titre: string; detail: string; corrigible: boolean; corrigeLe?: string | null; erreurCorrection?: string | null }
interface Correction { type: string; apres: string; date: string; mode: string }
interface Visibilite {
  libelle?: string | null;
  noteGoogle?: number | null;
  avisTotal?: number | null;
  nbConcurrentsCompares: number;
  meilleurQueNote: number;
  totalNote: number;
  meilleurQueAvis: number;
  totalAvis: number;
}
interface Diagnostic {
  ficheConnectee: boolean;
  ecarts: Ecart[];
  corrections: Correction[];
  derniereVerification?: string | null;
  optimisationAuto: boolean;
  visibilite?: Visibilite | null;
}

const dateCourte = (d: string) => new Date(d).toLocaleDateString('fr-FR');

export default function FicheGooglePage() {
  const [diagnostic, setDiagnostic] = useState<Diagnostic | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  const charger = useCallback(async (forcer = false) => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await fetch(`/api/fiche-gmb/diagnostic${forcer ? '?force=1' : ''}`, { headers: h() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Diagnostic indisponible');
      setDiagnostic({ ...d, ecarts: d.ecarts || [], corrections: d.corrections || [] });
    } catch (e: any) {
      setErreur(e.message || 'Diagnostic indisponible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const corriger = async (type: string) => {
    setEnCours(type);
    setErreur(null);
    try {
      const r = await fetch('/api/fiche-gmb/corriger', { method: 'POST', headers: h(), body: JSON.stringify({ type }) });
      if (!r.ok) throw new Error();
      await charger(true);
    } catch {
      setErreur('La correction a échoué, réessayez.');
    } finally {
      setEnCours(null);
    }
  };

  const changerOptimisation = async (auto: boolean) => {
    const r = await fetch('/api/auth/gmb-optimisation', { method: 'PUT', headers: h(), body: JSON.stringify({ auto }) });
    if (r.ok) setDiagnostic(d => d && { ...d, optimisationAuto: auto });
    else setErreur("Le choix n'a pas pu être enregistré.");
  };

  if (chargement && !diagnostic) return <p className="text-sm text-gray-500">Analyse de votre fiche en cours…</p>;
  if (!diagnostic) return <p className="text-sm text-gray-500">{erreur}</p>;
  if (!diagnostic.ficheConnectee) {
    return <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-500">Connectez votre fiche Google Business dans Réseaux sociaux pour lancer le diagnostic.</div>;
  }

  const ouverts = diagnostic.ecarts.filter(e => !e.corrigeLe);
  const v = diagnostic.visibilite;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {erreur && <p className="text-sm text-red-600">{erreur}</p>}

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{diagnostic.optimisationAuto ? 'EvoClick optimise votre fiche' : 'Vous gérez votre fiche vous-même'}</p>
          <p className="text-xs text-gray-500 mt-0.5">Correction automatique des écarts détectés.</p>
        </div>
        <input type="checkbox" aria-label="Optimisation automatique" checked={diagnostic.optimisationAuto} onChange={e => changerOptimisation(e.target.checked)} className="accent-violet-600 w-5 h-5" />
      </div>

      {v && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-sm font-semibold text-gray-900">{v.libelle || 'Votre visibilité'}</p>
          <div className="grid grid-cols-2 gap-3 mt-3 text-sm text-gray-600">
            <p><span className="text-xl font-bold text-gray-900">{v.noteGoogle != null ? v.noteGoogle.toLocaleString('fr-FR') : '—'}</span> ★<br />Mieux noté que {v.meilleurQueNote} sur {v.totalNote}</p>
            <p><span className="text-xl font-bold text-gray-900">{v.avisTotal ?? '—'}</span> avis<br />Plus d'avis que {v.meilleurQueAvis} sur {v.totalAvis}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="font-semibold text-gray-900">{ouverts.length === 0 ? 'Votre fiche est à jour' : 'Diagnostic de votre fiche'}</p>
          <button disabled={chargement} onClick={() => charger(true)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">{chargement ? 'Analyse…' : 'Relancer'}</button>
        </div>
        {ouverts.length === 0 && <p className="text-sm text-gray-500 mt-2">Aucun écart détecté face à vos concurrents les mieux notés.</p>}
        <div className="space-y-3 mt-3">
          {ouverts.map(e => (
            <div key={e.type} className="rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-semibold text-gray-800">{e.titre}</p>
              <p className="text-xs text-gray-500 mt-1">{e.detail}</p>
              {e.erreurCorrection && <p className="text-xs text-red-600 mt-1">La dernière tentative automatique a échoué : {e.erreurCorrection}</p>}
              {e.corrigible && (diagnostic.optimisationAuto
                ? <p className="text-xs font-semibold text-violet-700 mt-2">Sera corrigé automatiquement</p>
                : <button disabled={enCours !== null} onClick={() => corriger(e.type)} className="mt-2 px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">{enCours === e.type ? 'Correction…' : 'Corriger maintenant'}</button>)}
            </div>
          ))}
        </div>

        {diagnostic.corrections.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-800 mb-1">Dernières corrections</p>
            {diagnostic.corrections.slice(0, 5).map((c, i) => (
              <p key={`${c.type}-${i}`} className="text-xs text-gray-500">{dateCourte(c.date)} · {c.apres}</p>
            ))}
          </div>
        )}
        {diagnostic.derniereVerification && <p className="text-xs text-gray-400 mt-3">Vérifié le {dateCourte(diagnostic.derniereVerification)}</p>}
      </div>
    </div>
  );
}
